import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

// Currency configuration
export const CURRENCIES = {
    USD: { symbol: '$', label: 'US Dollar', code: 'USD' },
    GBP: { symbol: '£', label: 'British Pound', code: 'GBP' },
    PKR: { symbol: 'PKR', label: 'Pakistani Rupee', code: 'PKR' },
    INR: { symbol: '₹', label: 'Indian Rupee', code: 'INR' }
};

// Create context
const CurrencyContext = createContext();

// Currency provider component
export const CurrencyProvider = ({ children }) => {
    const { user } = useAuth();

    // Get current currency from user settings, default to USD
    const currentCurrency = user?.settings?.currency || 'USD';
    const currencyConfig = CURRENCIES[currentCurrency];

    // Format currency function
    const formatCurrency = (amount, options = {}) => {
        const {
            showCode = false,
            precision = 2,
            showSymbol = true
        } = options;

        if (amount === null || amount === undefined || isNaN(amount)) {
            return showSymbol ? `${currencyConfig.symbol}0.00` : '0.00';
        }

        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        const formattedAmount = numericAmount.toFixed(precision);

        if (showCode) {
            return `${formattedAmount} ${currencyConfig.code}`;
        }

        if (showSymbol) {
            // For PKR, place symbol after the amount
            if (currentCurrency === 'PKR') {
                return `${formattedAmount} ${currencyConfig.symbol}`;
            }
            // For other currencies, place symbol before the amount
            return `${currencyConfig.symbol}${formattedAmount}`;
        }

        return formattedAmount;
    };

    const value = {
        currentCurrency,
        currencyConfig,
        formatCurrency,
        currencies: CURRENCIES
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

// Custom hook to use currency context
export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

export default CurrencyContext;
